'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import CatalogRepository from '@/creditflowresearch/infrastructure/repository/CatalogRepository';
import ICatalogCollection from '../common';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';

interface ISponsorsCatalogContext {
  reset: Function,
  isFetching: boolean,
  sponsorsList: ICatalogCollection,
}

export const CatalogSponsorsContext = createContext(null);

export const useSponsors = (serverSponsors: ICatalogCollection): ISponsorsCatalogContext => {
  const [sponsorsList, setSponsorsList] = useState<ICatalogCollection>(serverSponsors);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const container = getContainer();
  const getSponsorsPageData = async () => {
    setIsFetching(true);
    try {
      const requestService = container.get(CatalogRepository);
      const frontendSponsors: ICatalogCollection = await requestService.getSponsorsCatalog();
      setSponsorsList(frontendSponsors);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
      setSponsorsList({});
    } finally {
      setIsFetching(false);
    }
  };

  const reset = useCallback(() => {
    setSponsorsList({});
  }, []);

  useEffect(() => {
    if (!Object.keys(serverSponsors).length) {
      getSponsorsPageData();
    }
  }, []);
  return {
    reset,
    isFetching,
    sponsorsList,
  };
};

export const useCatalogSponsorsContext = (): ISponsorsCatalogContext => {
  const context = useContext(CatalogSponsorsContext);
  if (!context) {
    throw new Error('useCatalogSponsorsContext must be used within the CatalogSponsorsContext');
  }
  return context;
};

export const CatalogSponsorsProvider = ({ children, sponsors }) => (
  <CatalogSponsorsContext.Provider value={ useSponsors(sponsors) }>
    { children }
  </CatalogSponsorsContext.Provider>
);
